import { useEffect, useMemo, useState } from "react";
import { useClasseContext } from "../../context/ClasseContext";
import { NavLink, useParams, useLocation } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { formatLettresDate } from "../../utils/ClasseEvaluations";
import tanoEdLogo from '../../assets/logoTanoEd.png';
import { Home, ChevronRight, Users, ChevronDown, School, FileBarChart, Building, Upload } from "lucide-react";
import { useComptesGarEnAttente } from '../../hooks/fetch_api_hook/useComptesGarEnAttente';
import {
    navItemClasses,
    subNavItemClasses,
    navItemStyle,
    toggleButtonClasses,
    ActiveBar,
    iconClasses,
} from './sidebarStyles';

function SideBarChef({ handleHomeClick, periode, anneescolaire }) {
    const { classeNom, groupNom, nomChef, ActivePeriode, setActivePeriode } = useClasseContext();
    const [classeSelection] = useState('');
    const { idclasse } = useParams();
    const periods_label = ["P1", "P2", "P3", "P4", "P5"];
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const { matiereFilter, setMatiereFilter, etablissementData } = useUser();

    const [menuOverride, setMenuOverride] = useState(null);
    const location = useLocation();

    const routeMenu = useMemo(() => {
        const path = location.pathname;
        const schoolRoutes = ['professeur_gestion', 'periode_scolaire'];
        const nationalRoutes = ['eval-nat-niveau', 'ecoles/', 'evolution/'];
        // « Espace classe » regroupe le Pilotage (Tableau de bord / Résultats
        // nationaux / Savoirs) + Élèves ; eval-nat/:id est un onglet du Pilotage classe.
        const classeRoutes = [`detail/${idclasse}`, `eleves/${idclasse}`, `savoirs/${idclasse}`, `eval-nat/${idclasse}`];

        if (classeRoutes.some(r => path.includes(r))) return 'classe';
        if (schoolRoutes.some(r => path.includes(r))) return 'school';
        if (nationalRoutes.some(r => path.includes(r))) return 'national';
        return 'none';
    }, [location.pathname, idclasse]);

    useEffect(() => { setMenuOverride(null); }, [location.pathname]);

    const isClasseOpen = menuOverride !== null ? menuOverride === 'classe' : routeMenu === 'classe';
    // « Pilotage » classe = un item → page à onglets (PilotageTabs) ; surligné sur
    // l'ensemble de ses routes (Tableau de bord / Savoirs / Résultats nationaux).
    const isPilotageClasseActive = [`detail/${idclasse}`, `savoirs/${idclasse}`, `eval-nat/${idclasse}`].some(r => location.pathname.includes(r));

    const handleClickPeriod = (period) => {
        if (selectedPeriod === period) {
            setSelectedPeriod(null);
            setActivePeriode(anneescolaire);
        } else {
            setSelectedPeriod(period);
            setActivePeriode(period);
        }
    };

    const toggleClasseMenu = () => setMenuOverride(isClasseOpen ? 'none' : 'classe');
    const handleHomeSideBarChefClick = () => setMenuOverride('none');

    const currentClasse = etablissementData?.classes?.find(c => c.id === parseInt(idclasse));
    const hasEleves = currentClasse ? currentClasse.nombre_eleves > 0 : true;

    // Badge « comptes GAR en attente d'import » (filtré périmètre côté back)
    const { data: enAttente } = useComptesGarEnAttente(
        etablissementData?.id, !!etablissementData?.is_gar);

    return (
        <div className="w-full flex flex-col">
            {/* Brand header — typographie Inter (design Tanoed) */}
            <div className="px-5 pt-5 pb-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center backdrop-blur-sm shrink-0 bg-white/15">
                    <img src={tanoEdLogo} alt="logo" className="w-7 h-7" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-semibold leading-tight text-white">Espace chef d'établissement</div>
                    <div className="text-[12px] text-blue-200/80 truncate">{nomChef}</div>
                </div>
            </div>

            {/* Établissement — pill compacte adaptée au fond bleu (cohérent avec Sidebar.jsx) */}
            <div className="mx-5 mb-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 border border-white/10 backdrop-blur-sm">
                <Building className="w-4 h-4 text-blue-200/70 shrink-0" />
                <span className="text-[12px] text-white/90 truncate">{etablissementData?.nom}</span>
            </div>

            {/* Accueil */}
            <div className="flex-1 px-3 py-2">
                <NavLink to="/home/" onClick={handleHomeSideBarChefClick} end className={navItemClasses} style={navItemStyle}>
                    {({ isActive }) => (
                        <>
                            {isActive && <ActiveBar />}
                            <Home size={18} className={iconClasses(isActive)} />
                            <span className="text-[14px] font-medium leading-tight flex-1">Accueil</span>
                        </>
                    )}
                </NavLink>
            </div>

            {!classeSelection && !idclasse ? (
                <div>
                    {/* Gestion établissement — un item → page à onglets
                        (Gestion des professeurs / Périodes scolaires) */}
                    <div className="flex-1 px-3 py-2">
                        <NavLink
                            to="professeur_gestion"
                            className={() => navItemClasses({ isActive: routeMenu === 'school' })}
                            style={() => navItemStyle({ isActive: routeMenu === 'school' })}
                        >
                            {routeMenu === 'school' && <ActiveBar />}
                            <School size={18} className={iconClasses(routeMenu === 'school')} />
                            <span className="text-[14px] font-medium leading-tight flex-1">Gestion établissement</span>
                        </NavLink>
                    </div>

                    {/* Pilotage des évals nationales — un item → page à onglets
                        (Résultats des évaluations / École secteur / Évolution des résultats) */}
                    <div className="flex-1 px-3 py-2">
                        <NavLink
                            to="eval-nat-niveau"
                            className={() => navItemClasses({ isActive: routeMenu === 'national' })}
                            style={() => navItemStyle({ isActive: routeMenu === 'national' })}
                        >
                            {routeMenu === 'national' && <ActiveBar />}
                            <FileBarChart size={18} className={iconClasses(routeMenu === 'national')} />
                            <span className="text-[14px] font-medium leading-tight flex-1">Pilotage des évals nationales</span>
                        </NavLink>
                    </div>

                    {/* Import établissement — wizard unique (spec GAR §10) */}
                    <div className="flex-1 px-3 py-2">
                        <NavLink
                            to="import-etablissement"
                            className={() => navItemClasses({ isActive: location.pathname.includes('import-etablissement') })}
                            style={() => navItemStyle({ isActive: location.pathname.includes('import-etablissement') })}
                        >
                            {location.pathname.includes('import-etablissement') && <ActiveBar />}
                            <Upload size={18} className={iconClasses(location.pathname.includes('import-etablissement'))} />
                            <span className="text-[14px] font-medium leading-tight flex-1">Import des élèves</span>
                            {enAttente?.count > 0 ? (
                                <span
                                    className="min-w-[18px] h-[18px] inline-flex items-center justify-center px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold"
                                    title={`${enAttente.count} élève(s) connecté(s) via l'ENT sans fiche — en attente d'import ou d'arbitrage`}
                                >
                                    {enAttente.count}
                                </span>
                            ) : null}
                        </NavLink>
                    </div>
                </div>
            ) : (
                <>
                    {/* Espace classe (toggle) */}
                    <div className="flex-1 px-3 py-2">
                        <button onClick={toggleClasseMenu} className={toggleButtonClasses(isClasseOpen)}>
                            <Users size={18} className={iconClasses(isClasseOpen)} />
                            <span className="text-[14px] font-medium leading-tight flex-1">Espace classe {classeNom}</span>
                            {isClasseOpen
                                ? <ChevronDown size={16} className="text-slate-400" />
                                : <ChevronRight size={16} className="text-slate-400" />}
                        </button>
                        <ul className={`toggle-menu ${isClasseOpen ? 'open' : ''}`}>
                            {/* Pilotage — un item → page à onglets (Tableau de bord,
                                Résultats nationaux, Savoirs via PilotageTabs) */}
                            <li className="ml-6 pl-3 border-l border-blue-300/30 py-1.5 mt-1">
                                <NavLink
                                    to={`detail/${idclasse}`}
                                    className={() => subNavItemClasses({ isActive: isPilotageClasseActive })}
                                    style={() => navItemStyle({ isActive: isPilotageClasseActive })}
                                >
                                    {isPilotageClasseActive && <ActiveBar />}
                                    <span className="text-[13px] font-medium leading-tight flex-1">Pilotage</span>
                                </NavLink>
                            </li>
                            {hasEleves && (
                                <li className="ml-6 pl-3 border-l border-blue-300/30 py-1.5">
                                    <NavLink to={`eleves/${idclasse}`} className={subNavItemClasses} style={navItemStyle}>
                                        {({ isActive }) => (
                                            <>
                                                {isActive && <ActiveBar />}
                                                <span className="text-[13px] font-medium leading-tight flex-1">Élèves</span>
                                            </>
                                        )}
                                    </NavLink>
                                </li>
                            )}
                        </ul>
                    </div>

                    {/* Bloc info classe + filtres */}
                    <div className="p-4 xl:p-2 3xl:p-4">
                        <div
                            style={{
                                background: `linear-gradient(to right, rgb(30 41 59 / 0.6), rgb(51 65 85 / 0.4), rgb(71 85 105 / 0.3))`
                            }}
                            className="rounded-lg p-4 xl:p-2 3xl:p-4 space-y-3 xl:space-y-1 3xl:space-y-3 shadow-md"
                        >
                            <div className="text-white font-bold text-sm xl:text-xs 3xl:text-sm mb-1">{classeNom ? `Classe - ${classeNom}` : `Groupe - ${groupNom}`}</div>
                            <div className="text-orange-300 text-sm xl:text-xs 3xl:text-sm mb-2 xl:mb-1 3xl:mb-2">Filtrage actif - <span className="text-orange-400">{ActivePeriode && ActivePeriode.type_periode + " " + (ActivePeriode.numero ? ActivePeriode.numero : "")}</span></div>
                            <div className="text-blue-200 text-xs mb-1">Date de début : {ActivePeriode && formatLettresDate(ActivePeriode.date_debut)}</div>
                            <div className="text-blue-200 text-xs">Date de fin : {ActivePeriode && formatLettresDate(ActivePeriode.date_fin)}</div>

                            <div className="h-5 xl:h-2 3xl:h-5" />

                            {etablissementData?.periodes_scolaires?.length > 0 && (
                                <div>
                                    <p className="text-xs font-medium xl:text-xs 3xl:text-sm">Le {etablissementData.periodes_scolaires.length > 2 ? "trimestre" : "semestre"}</p>
                                    <div className={`grid ${etablissementData.periodes_scolaires.length <= 3 ? 'grid-cols-3' : 'grid-cols-4'} gap-2`}>
                                        {etablissementData.periodes_scolaires.map((p, index) => (
                                            <div
                                                key={index}
                                                onClick={() => handleClickPeriod(p)}
                                                className={`px-2 py-1 text-xs rounded border cursor-pointer transition-colors duration-200 text-center
                                                    ${selectedPeriod === p
                                                        ? "bg-orange-500 text-white border-orange-400"
                                                        : "bg-blue-600/60 text-white border-blue-400 hover:bg-blue-600"}`}
                                            >
                                                <p className="font-bold">{p.type_periode[0] + p.numero}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <p className="text-xs font-medium xl:text-xs 3xl:text-sm mt-4 xl:mt-2 3xl:mt-4">La période</p>
                            <div className="grid grid-cols-3 gap-2">
                                {periode.slice(0, 3).map((p, index) => (
                                    <div
                                        key={index}
                                        onClick={() => handleClickPeriod(p)}
                                        className={`px-2 py-1 text-xs rounded border cursor-pointer transition-colors duration-200 text-center
                                            ${selectedPeriod === p
                                                ? "bg-orange-500 text-white border-orange-400"
                                                : "bg-blue-600/60 text-white border-blue-400 hover:bg-blue-600"}`}
                                    >
                                        <p className="font-bold">{periods_label[index]}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-2 gap-2 mt-2">
                                {periode.slice(3, 5).map((p, index) => (
                                    <div
                                        key={index + 3}
                                        onClick={() => handleClickPeriod(p)}
                                        className={`px-2 py-1 text-xs rounded border cursor-pointer transition-colors duration-200 text-center
                                            ${selectedPeriod === p
                                                ? "bg-orange-500 text-white border-orange-400"
                                                : "bg-blue-600/60 text-white border-blue-400 hover:bg-blue-600"}`}
                                    >
                                        <p className="font-bold">{periods_label[index + 3]}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="flex flex-col mt-4 xl:mt-2 3xl:mt-4">
                                <p className="text-xs font-medium xl:text-xs 3xl:text-sm">La matière</p>
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                    {["maths", "français"].map((m) => (
                                        <button
                                            key={m}
                                            onClick={() => setMatiereFilter(m)}
                                            className={`px-2 py-1 text-xs rounded border cursor-pointer transition-colors duration-200 text-center
                                                ${matiereFilter === m
                                                    ? "bg-orange-500 text-white border-orange-400"
                                                    : "bg-blue-600/60 text-white border-blue-400 hover:bg-blue-600"}`}
                                        >
                                            {m === "maths" ? "Mathématiques" : "Français"}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default SideBarChef;
